import { useState } from 'react'
import { useWatch, useFormContext } from 'react-hook-form'
import {
  TextInput,
  ImageInput,
  maxLength,
  minValue,
  maxValue,
  useInput,
  useNotify,
} from 'react-admin'
import axios from 'axios'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import FormHelperText from '@mui/material/FormHelperText'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import Tooltip from '@mui/material/Tooltip'

import CustomTextInput from '../../components/CustomTextInput'
import CustomFileInput from '../../components/CustomFileInput'
import CustomFileField from '../../components/CustomFileField'
import CustomImageField from '../../components/CustomImageField'
import { validateRequired, validateUnique } from '../../utils/validate'
import { ROLES } from '../../components/constants'
import feathersClient from '../../feathersClient'

const validateEpisodeOrder = [
  validateRequired('Order'),
  minValue(1),
  maxValue(1000),
]

const extractVideoUrl = videoUrl => {
  if (typeof videoUrl === 'string')
    return { url: videoUrl, duration: null, isNew: false }
  if (!videoUrl) return { url: null, duration: null, isNew: false }
  const rawFile = videoUrl.rawFile
  if (!rawFile) return { url: null, duration: null, isNew: false }
  if (!rawFile.videoUrl)
    throw 'Episode video is still uploading, please wait...'
  return {
    url: rawFile.videoUrl.url,
    duration: rawFile.videoUrl.duration,
    isNew: true,
  }
}

const uploadCoverImage = async (coverImageUrl, storageKey, episodeIndex) => {
  if (!coverImageUrl || typeof coverImageUrl === 'string')
    return coverImageUrl || null
  const file = coverImageUrl.rawFile
  if (!file || !(file instanceof File)) return null
  const fileExtension = file.name.split('.').pop()
  const fileName = `${storageKey}/episodeCoverImage/${episodeIndex}-${Date.now()}.${fileExtension}`
  const { signedUrl, contentType } = await feathersClient
    .get()
    .service('upload-files')
    .create({ fileName, fileExtension })
  await axios.put(signedUrl, file, { headers: { 'Content-Type': contentType } })
  return fileName
}

const EpisodesInput = ({ permissions }) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [saving, setSaving] = useState(false)
  const [removingIndex, setRemovingIndex] = useState(null)

  const notify = useNotify()

  const {
    fieldState: { error },
  } = useInput({
    source: 'episodes',
    validate: [validateRequired('Episodes'), validateUnique('order', true)],
    defaultValue: [],
  })

  const { setValue, getValues } = useFormContext()
  const episodes = useWatch({ name: 'episodes' }) || []
  const courseId = useWatch({ name: 'id' })
  const courseStorageKey = useWatch({ name: 'storageKey' })

  const handleAdd = () => {
    const current = getValues('episodes') || []
    setValue(
      'episodes',
      [
        ...current,
        { order: current.length + 1, title: '', shortDescription: '' },
      ],
      { shouldDirty: true },
    )
    setSelectedIndex(current.length)
  }

  const handleRemove = async (index, e) => {
    e.stopPropagation()
    const current = getValues('episodes') || []
    const episode = current[index]

    if (episode?.id) {
      setRemovingIndex(index)
      try {
        await feathersClient.get().service('course-episodes').remove(episode.id)
      } catch (err) {
        notify(typeof err === 'string' ? err : 'Failed to remove episode', {
          type: 'error',
        })
        setRemovingIndex(null)
        return
      }
      setRemovingIndex(null)
    }

    const updated = current.filter((_, i) => i !== index)
    setValue('episodes', updated, { shouldDirty: true })
    setSelectedIndex(prev => Math.min(prev, Math.max(0, updated.length - 1)))
  }

  const saveEpisode = async () => {
    const current = getValues('episodes') || []
    const episode = current[selectedIndex]
    if (!episode) return

    try {
      setSaving(true)

      const {
        url: videoUrl,
        duration,
        isNew,
      } = extractVideoUrl(episode.videoUrl)

      const coverImageUrl = await uploadCoverImage(
        episode.coverImageUrl,
        courseStorageKey || `courses/${courseId}`,
        selectedIndex,
      )

      const episodeStorageKey =
        episode.storageKey ||
        (courseStorageKey
          ? `${courseStorageKey}/episodes/${Date.now()}`
          : undefined)

      const payload = {
        courseId,
        order: episode.order,
        title: episode.title,
        shortDescription: episode.shortDescription,
        ...(videoUrl && { videoUrl }),
        ...(duration != null && { duration }),
        ...(isNew && { transcodeStatus: 'New' }),
        ...(episodeStorageKey && { storageKey: episodeStorageKey }),
        ...(coverImageUrl && { coverImageUrl }),
      }

      let savedEpisode
      if (episode.id) {
        savedEpisode = await feathersClient
          .get()
          .service('course-episodes')
          .patch(episode.id, payload)
      } else {
        savedEpisode = await feathersClient
          .get()
          .service('course-episodes')
          .create(payload)
      }

      const updated = [...current]
      updated[selectedIndex] = {
        ...episode,
        id: savedEpisode.id,
        storageKey: savedEpisode.storageKey || episodeStorageKey,
        ...(coverImageUrl && { coverImageUrl }),
      }
      setValue('episodes', updated, { shouldDirty: true })

      notify('Episode saved successfully', { type: 'success' })
    } catch (err) {
      notify(typeof err === 'string' ? err : 'Failed to save episode', {
        type: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const selectedEpisode = episodes[selectedIndex]

  return (
    <Box mt={2} mb={2} width="100%">
      <Typography variant="subtitle1" fontWeight={500} gutterBottom>
        <h3>Episodes</h3>
      </Typography>
      {error && (
        <FormHelperText error sx={{ mb: 1 }}>
          {error.message}
        </FormHelperText>
      )}
      <Box
        display="flex"
        sx={{
          width: '100%',
          border: 1,
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 1,
          minHeight: 450,
          overflow: 'hidden',
        }}>
        {/* Left Panel - Episode List */}
        <Box
          sx={{
            width: 240,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Box
            sx={{
              p: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Episodes
            </Typography>
            <Tooltip title="Add episode">
              <IconButton size="small" onClick={handleAdd} color="primary">
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              maxHeight: 500,
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}>
            {episodes.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                No episodes yet
              </Typography>
            )}
            {episodes.map((_, index) => {
              const isActive = selectedIndex === index
              return (
                <Box
                  key={index}
                  onClick={() => setSelectedIndex(index)}
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    border: '1.5px solid',
                    borderColor: isActive ? '#c5c1c1ff' : '#e8e8e8',
                    cursor: 'pointer',
                    backgroundColor: isActive ? '#f5f5f5' : 'white',
                    boxShadow: isActive
                      ? '0 2px 8px rgba(0,0,0,0.1)'
                      : '0 1px 3px rgba(0,0,0,0.04)',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: '#c5c1c1ff',
                      backgroundColor: '#fafcff',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      color="text.primary"
                      fontWeight={700}
                      display="block"
                      noWrap>
                      {episodes[index]?.order ?? index + 1}.{' '}
                      {episodes[index]?.title || 'Untitled'}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={e => handleRemove(index, e)}
                    color="error"
                    disabled={removingIndex === index}
                    sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}>
                    {removingIndex === index ? (
                      <CircularProgress size={14} color="error" />
                    ) : (
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    )}
                  </IconButton>
                </Box>
              )
            })}
          </Box>
        </Box>

        {/* Right Panel - Episode Details */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
          {episodes.length === 0 ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%">
              <Typography color="text.secondary">
                Click "Add Episode" to get started
              </Typography>
            </Box>
          ) : (
            <>
              {/* Scrollable fields */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {episodes.map((_, index) => (
                  <Box
                    key={index}
                    display={selectedIndex === index ? 'block' : 'none'}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'flex-start',
                      }}>
                      <TextInput
                        source={`episodes.${index}.order`}
                        label="Order"
                        variant="standard"
                        validate={validateEpisodeOrder}
                        sx={{ width: 80, flexShrink: 0 }}
                      />
                      <CustomTextInput
                        source={`episodes.${index}.title`}
                        label="Title"
                        fullWidth
                        variant="standard"
                        helperText={'Tối đa 255 kí tự, lý tưởng < 50 kí tự'}
                        validate={[
                          validateRequired('title'),
                          maxLength(
                            255,
                            'Do not enter more than 255 characters',
                          ),
                        ]}
                        showTextLength
                      />
                    </Box>
                    <CustomTextInput
                      source={`episodes.${index}.shortDescription`}
                      label="Description"
                      fullWidth
                      variant="standard"
                      rows={3}
                      multiline
                      validate={[
                        maxLength(300, 'Do not enter more than 300 characters'),
                      ]}
                      showTextLength
                    />
                    <CustomFileInput
                      source={`episodes.${index}.videoUrl`}
                      label="File"
                      type="episode"
                      accept="video/*"
                      upload={true}
                      showProgress
                      validate={validateRequired('Episode file')}>
                      <CustomFileField
                        source="src"
                        title="episode"
                        variant="standard"
                        canDownload={permissions === ROLES.ADMIN}
                      />
                    </CustomFileInput>
                    <ImageInput
                      source={`episodes.${index}.coverImageUrl`}
                      label="Cover image"
                      accept="image/*"
                      multiple={false}
                      helperText="Tỉ lệ 2:3">
                      <CustomImageField source="src" title="Cover image" />
                    </ImageInput>
                  </Box>
                ))}
              </Box>

              {/* Footer - Save button */}
              <Box
                sx={{
                  p: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  flexShrink: 0,
                }}>
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={saveEpisode}
                  disabled={!courseId || saving || !selectedEpisode}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default EpisodesInput
